import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { useFloatingCards } from "@/context/FloatingCards";
import { useRealtime } from "@/hooks/useRealtime";
import { useHotkeys } from "@/hooks/useHotkeys";
import { useBoard } from "@/hooks/useData";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { TopBar } from "@/components/desktop/TopBar";
import { PendingQueue } from "@/components/board/PendingQueue";
import { ActiveCalls } from "@/components/board/ActiveCalls";
import { UnitBoard } from "@/components/board/UnitBoard";
import { FinishedStrip } from "@/components/board/FinishedStrip";
import { CallWorkspace } from "@/components/workspace/CallWorkspace";
import { CommandBar } from "@/components/command/CommandBar";
import { SafetyTimers } from "@/components/SafetyTimers";
import { OfficerInitiatedCard } from "@/components/cards/OfficerInitiatedCard";
import { SearchCard } from "@/components/cards/SearchCard";
import { MapCard } from "@/components/cards/MapCard";
import { EntitySearchCard } from "@/components/cards/EntitySearchCard";
import { PreferencesCard } from "@/components/cards/PreferencesCard";

export default function DispatchPage() {
  const { user } = useAuth();
  const { prefs, saveLayout, hotkeys, loaded } = usePrefs();
  const realtime = useRealtime(!!user);
  const qc = useQueryClient();
  const cards = useFloatingCards();
  const boardQ = useBoard();
  const draftsQ = useQuery({ queryKey: ["drafts"], queryFn: async () => (await api.get("/incidents/drafts")).data });
  const [selectedId, setSelectedId] = useState(() => sessionStorage.getItem("cad_selected") || null);

  const select = useCallback((id) => { setSelectedId(id); if (id) sessionStorage.setItem("cad_selected", id); else sessionStorage.removeItem("cad_selected"); }, []);

  const newDraft = useCallback(async () => {
    try {
      const { data } = await api.post("/incidents/draft");
      qc.setQueryData(["incident", data.id], { ...data, comments: [], assignments: [], recommendation: null, location_context: null });
      qc.invalidateQueries({ queryKey: ["drafts"] });
      select(data.id);
      toast.success("Draft opened — received time captured", { duration: 2000 });
    } catch (e) { toast.error(errMsg(e)); }
  }, [qc, select]);

  const openOfficerInitiated = useCallback(() => cards.open("officer-initiated", OfficerInitiatedCard, { onCreated: select }, { width: 600, x: 240, y: 90 }), [cards, select]);
  const openSearch = useCallback(() => cards.open("search", SearchCard, { onOpen: select }, { width: 1000, x: 160, y: 70 }), [cards, select]);
  const openMap = useCallback((id) => cards.open("map", MapCard, { incidentId: id ?? selectedId }, { width: 680, x: 300, y: 80 }), [cards, selectedId]);
  const openEntitySearch = useCallback(() => cards.open("entity-search", EntitySearchCard, { onOpen: select }, { width: 760, x: 200, y: 90 }), [cards, select]);
  const openPreferences = useCallback(() => cards.open("preferences", PreferencesCard, {}, { width: 520, x: 700, y: 70 }), [cards]);
  const focusCommand = useCallback(() => window.dispatchEvent(new Event("cad:focus-command")), []);

  const handlers = useMemo(() => ({ new_call: newDraft, officer_initiated: openOfficerInitiated, search: openSearch, entity_search: openEntitySearch, map: () => openMap(), command_bar: focusCommand }), [newDraft, openOfficerInitiated, openSearch, openEntitySearch, openMap, focusCommand]);
  useHotkeys(hotkeys, handlers);

  const board = boardQ.data;
  const units = board?.units || [];
  const statuses = board?.statuses || [];
  const selectedLive = useMemo(() => [...(board?.pending || []), ...(board?.active || [])].find((i) => i.id === selectedId) || null, [board, selectedId]);

  const dropUnit = useCallback(async (incidentId, unitId) => {
    try {
      const { data } = await api.post(`/incidents/${incidentId}/assign`, { unit_ids: [unitId], via: "drag-drop" });
      toast.success(`${data.assigned.join(", ")} assigned to ${data.incident_number} (drag & drop)`);
      select(incidentId);
    } catch (e) { toast.error(errMsg(e)); }
  }, [select]);
  const assignToSelected = useCallback((unitId) => { if (selectedLive) dropUnit(selectedLive.id, unitId); }, [selectedLive, dropUnit]);

  useEffect(() => { if (boardQ.error) toast.error("Board refresh failed — retrying", { id: "board-error" }); }, [boardQ.error]);

  const layout = prefs.layout || {};
  const sz = (key, idx, def) => layout[key]?.[idx] ?? def;

  return (
    <div className="grid-desktop h-screen flex flex-col" data-testid="dispatch-page">
      <TopBar onNewCall={newDraft} onOfficerInitiated={openOfficerInitiated} onSearch={openSearch} onMap={() => openMap()} onEntitySearch={openEntitySearch} onPreferences={openPreferences} realtime={realtime} />
      <div className="flex-1 min-h-0 p-2">
        {loaded && (
          <ResizablePanelGroup direction="horizontal" onLayout={(s) => saveLayout("main", s)} className="gap-2">
            <ResizablePanel defaultSize={sz("main", 0, 50)} minSize={30}>
              <ResizablePanelGroup direction="vertical" onLayout={(s) => saveLayout("left", s)} className="gap-2">
                <ResizablePanel defaultSize={sz("left", 0, 26)} minSize={10}>
                  <PendingQueue items={board?.pending || []} selectedId={selectedId} onSelect={select} onDropUnit={dropUnit} />
                </ResizablePanel>
                <ResizableHandle className="bg-transparent hover:bg-primary/40 h-1 rounded" />
                <ResizablePanel defaultSize={sz("left", 1, 56)} minSize={25}>
                  <ResizablePanelGroup direction="horizontal" onLayout={(s) => saveLayout("middle", s)} className="gap-2">
                    <ResizablePanel defaultSize={sz("middle", 0, 42)} minSize={20}>
                      <ActiveCalls items={board?.active || []} selectedId={selectedId} onSelect={select} onDropUnit={dropUnit} />
                    </ResizablePanel>
                    <ResizableHandle className="bg-transparent hover:bg-primary/40 w-1 rounded" />
                    <ResizablePanel defaultSize={sz("middle", 1, 58)} minSize={25}>
                      <UnitBoard units={units} statuses={statuses} onOpenIncident={select} onAssignToSelected={assignToSelected} selectedLive={selectedLive} />
                    </ResizablePanel>
                  </ResizablePanelGroup>
                </ResizablePanel>
                <ResizableHandle className="bg-transparent hover:bg-primary/40 h-1 rounded" />
                <ResizablePanel defaultSize={sz("left", 2, 18)} minSize={8}>
                  <FinishedStrip items={board?.finished || []} selectedId={selectedId} onSelect={select} onSearch={openSearch} />
                </ResizablePanel>
              </ResizablePanelGroup>
            </ResizablePanel>
            <ResizableHandle className="bg-transparent hover:bg-primary/40 w-1 rounded" />
            <ResizablePanel defaultSize={sz("main", 1, 50)} minSize={30}>
              <CallWorkspace incidentId={selectedId} onSelect={select} onDeselect={() => select(null)} onNewCall={newDraft} units={units} statuses={statuses} drafts={draftsQ.data || []} onOpenMap={openMap} />
            </ResizablePanel>
          </ResizablePanelGroup>
        )}
      </div>
      <CommandBar onIncidentCreated={select} />
      <SafetyTimers timers={board?.timers || []} onOpenIncident={select} />
    </div>
  );
}
